import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ChevronLeft, ChevronRight, Plus, Clock, User, X, Trash2,
  RefreshCw, Wifi, WifiOff, Bell, BellOff, Loader2, AlertCircle,
} from 'lucide-react'
import { getCalendarEvents, createCalendarEvent, deleteCalendarEvent } from '../services/calendar.service'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useGoogleCalendar } from '../context/GoogleCalendarContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import { useDateRange } from '../context/DateRangeContext'
import {
  scheduleReminders, cancelReminders,
  scheduleAllReminders, requestNotificationPermission,
  notificationsGranted,
} from '../services/reminderService'
import type { CalendarEvent } from '../types'

const EVENT_COLORS: Record<CalendarEvent['type'], { bg: string; text: string }> = {
  meeting:  { bg: 'rgba(255,198,0,0.18)',   text: '#ffc600' },
  deadline: { bg: 'rgba(204,51,0,0.2)',     text: '#cc3300' },
  review:   { bg: 'rgba(255,140,0,0.18)',   text: '#ff8c00' },
  call:     { bg: 'rgba(232,160,0,0.18)',   text: '#e8a000' },
  holiday:  { bg: 'rgba(85,85,85,0.25)',    text: '#a0a0a0' },
}

const DAYS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

function getCalendarDays(year: number, month: number) {
  const first      = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInPrev  = new Date(year, month, 0).getDate()
  const cells: { date: Date; current: boolean }[] = []
  for (let i = first - 1; i >= 0; i--)
    cells.push({ date: new Date(year, month - 1, daysInPrev - i), current: false })
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), current: true })
  while (cells.length % 7 !== 0)
    cells.push({ date: new Date(year, month + 1, cells.length - daysInMonth - first + 1), current: false })
  return cells
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate()
}

function timeAgo(d: Date) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000)
  if (s < 10)  return 'just now'
  if (s < 60)  return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60)  return `${m}m ago`
  return `${Math.floor(m / 60)}h ago`
}

// ── Add Event Modal ────────────────────────────────────────────
function AddEventModal({
  onClose, onAdd, defaultDate, isSyncing,
}: {
  onClose: () => void
  onAdd: (e: Omit<CalendarEvent, 'id'>) => void
  defaultDate?: string
  isSyncing?: boolean
}) {
  const [form, setForm] = useState({
    title:       '',
    date:        defaultDate ?? new Date().toISOString().split('T')[0],
    time:        '10:00',
    type:        'meeting' as CalendarEvent['type'],
    clientName:  '',
    description: '',
  })
  const [error, setError] = useState('')
  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) { setError('Event title is required.'); return }
    onAdd({
      title:       form.title.trim(),
      date:        form.date,
      time:        form.time || undefined,
      type:        form.type,
      clientName:  form.clientName.trim() || undefined,
      description: form.description.trim() || undefined,
      source:      'erp',
    })
    onClose()
  }

  const inputCls   = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Add Event</h2>
            {isSyncing && (
              <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: '#ffc600' }}>
                <Loader2 size={10} className="animate-spin" /> Syncing to Google…
              </p>
            )}
          </div>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>EVENT TITLE *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. Strategy Call — Astra Fintech"
              className={inputCls} style={inputStyle} autoFocus
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DATE</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>TIME</label>
              <input type="time" value={form.time} onChange={e => set('time', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>TYPE</label>
              <select value={form.type} onChange={e => set('type', e.target.value)} className={inputCls} style={inputStyle}>
                <option value="meeting">Meeting</option>
                <option value="call">Call</option>
                <option value="review">Review</option>
                <option value="deadline">Deadline</option>
                <option value="holiday">Holiday</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CLIENT</label>
              <input value={form.clientName} onChange={e => set('clientName', e.target.value)}
                placeholder="Client name (optional)" className={inputCls} style={inputStyle}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DESCRIPTION</label>
            <input value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Optional notes"
              className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Add Event
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Event Popover ──────────────────────────────────────────────
function EventPopover({ events, date, onClose, onAddEvent, onDeleteEvent, isAdmin }: {
  events: CalendarEvent[]
  date: Date
  onClose: () => void
  onAddEvent: () => void
  onDeleteEvent: (id: string) => void
  isAdmin: boolean
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null)
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-xs rounded-2xl p-4"
        style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.3)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-white">
            {date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long' })}
          </p>
          <button onClick={onClose} style={{ color: '#555' }}><X size={15} /></button>
        </div>

        {events.length === 0 ? (
          <p className="text-xs text-center py-4" style={{ color: '#555' }}>No events</p>
        ) : (
          <div className="space-y-2 mb-3">
            {events.map(ev => {
              const col = EVENT_COLORS[ev.type]
              const isGoogle = ev.source === 'google'
              return (
                <div key={ev.id} className="rounded-lg overflow-hidden" style={{ background: '#1a1a1a' }}>
                  {confirmId === ev.id ? (
                    <div className="flex items-center gap-2 px-2 py-2">
                      <span className="text-xs flex-1" style={{ color: '#a0a0a0' }}>Delete this event?</span>
                      <button onClick={() => setConfirmId(null)}
                        className="text-xs px-2 py-1 rounded" style={{ background: '#2a2a2a', color: '#a0a0a0' }}>No</button>
                      <button onClick={() => { onDeleteEvent(ev.id); setConfirmId(null) }}
                        className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(204,51,0,0.2)', color: '#cc3300' }}>Yes</button>
                    </div>
                  ) : (
                    <div className="flex gap-2 p-2 items-start">
                      <div className="w-1 rounded-full flex-shrink-0 mt-1 self-stretch" style={{ background: col.text }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-semibold text-white truncate">{ev.title}</p>
                          {isGoogle && (
                            <span className="text-xs px-1 py-0.5 rounded flex-shrink-0"
                              style={{ background: 'rgba(66,133,244,0.15)', color: '#4285f4', fontSize: '9px', fontWeight: 600 }}>
                              G
                            </span>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: '#a0a0a0' }}>
                          {ev.time && `${ev.time} · `}
                          <span className="capitalize">{ev.type}</span>
                          {ev.clientName && ` · ${ev.clientName}`}
                        </p>
                      </div>
                      {isAdmin && (
                        <button onClick={() => setConfirmId(ev.id)}
                          className="p-1 flex-shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                          style={{ color: '#cc3300' }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {isAdmin && (
          <button onClick={onAddEvent}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.2)' }}>
            <Plus size={12} /> Add event on this day
          </button>
        )}
      </div>
    </div>
  )
}

// ── Google Sync Status Bar ─────────────────────────────────────
function SyncBar({
  connected, connecting, syncing, lastSynced, error, configured,
  onConnect, onDisconnect, onRefresh, notifGranted, onRequestNotif,
}: {
  connected: boolean; connecting: boolean; syncing: boolean
  lastSynced: Date | null; error: string | null; configured: boolean
  onConnect: () => void; onDisconnect: () => void; onRefresh: () => void
  notifGranted: boolean; onRequestNotif: () => void
}) {
  if (!configured) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-4"
        style={{ background: 'rgba(255,198,0,0.04)', border: '1px solid rgba(255,198,0,0.1)' }}>
        <AlertCircle size={14} style={{ color: '#555', flexShrink: 0 }} />
        <p className="text-xs" style={{ color: '#555' }}>
          Google Calendar not configured. Add <code className="font-mono">VITE_GOOGLE_CLIENT_ID</code> to your <code className="font-mono">.env</code> file.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl mb-4"
      style={{
        background: connected ? 'rgba(66,133,244,0.06)' : 'rgba(255,198,0,0.04)',
        border: `1px solid ${connected ? 'rgba(66,133,244,0.2)' : 'rgba(255,198,0,0.1)'}`,
      }}>

      {/* Status dot */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {connected ? (
          <Wifi size={14} style={{ color: '#4285f4', flexShrink: 0 }} />
        ) : (
          <WifiOff size={14} style={{ color: '#555', flexShrink: 0 }} />
        )}
        <div className="min-w-0">
          {connected ? (
            <span className="text-xs font-semibold" style={{ color: '#4285f4' }}>
              Google Calendar connected
              {syncing && <Loader2 size={10} className="inline animate-spin ml-1.5" />}
            </span>
          ) : (
            <span className="text-xs" style={{ color: '#555' }}>Google Calendar not connected</span>
          )}
          {lastSynced && !syncing && (
            <span className="text-xs ml-2" style={{ color: '#555' }}>· synced {timeAgo(lastSynced)}</span>
          )}
          {error && (
            <span className="text-xs ml-2" style={{ color: '#cc3300' }}>· {error}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Notification toggle */}
        <button
          onClick={onRequestNotif}
          title={notifGranted ? 'Reminders on' : 'Enable reminders'}
          className="p-1.5 rounded-lg transition-all"
          style={notifGranted
            ? { background: 'rgba(255,198,0,0.12)', color: '#ffc600' }
            : { background: '#1a1a1a', color: '#555' }}>
          {notifGranted ? <Bell size={13} /> : <BellOff size={13} />}
        </button>

        {connected ? (
          <>
            <button onClick={onRefresh} disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
              Sync now
            </button>
            <button onClick={onDisconnect}
              className="px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: '#1a1a1a', color: '#555', border: '1px solid rgba(255,198,0,0.08)' }}>
              Disconnect
            </button>
          </>
        ) : (
          <button onClick={onConnect} disabled={connecting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60"
            style={{ background: '#4285f4', color: '#fff' }}>
            {connecting
              ? <><Loader2 size={11} className="animate-spin" /> Connecting…</>
              : <>Connect Google Calendar</>
            }
          </button>
        )}
      </div>
    </div>
  )
}

// ── Main ───────────────────────────────────────────────────────
export default function CalendarPage() {
  const now = new Date()
  const [viewDate, setViewDate]     = useState({ year: now.getFullYear(), month: now.getMonth() })
  const [events, setEvents]         = useState<CalendarEvent[]>([])
  const [showAdd, setShowAdd]       = useState(false)
  const [addDate, setAddDate]       = useState<string | undefined>(undefined)
  const [dayPopover, setDayPopover] = useState<{ date: Date; events: CalendarEvent[] } | null>(null)
  const [notifGranted, setNotifGranted] = useState(notificationsGranted())

  const { toast }       = useToast()
  const { addActivity } = useActivity()
  const location        = useLocation()
  const gc              = useGoogleCalendar()
  const { inRange }     = useDateRange()
  const isAdmin         = useIsAdmin()

  // ── Load events from Supabase ─────────────────────────────────
  useEffect(() => {
    getCalendarEvents().then(setEvents).catch(console.error)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  // ── Schedule reminders on mount + when events change ──────────
  useEffect(() => {
    scheduleAllReminders(events)
  }, [events])

  // ── Merge Google events into ERP state ────────────────────────
  const mergeGoogleEvents = useCallback((googleEvents: CalendarEvent[]) => {
    setEvents(prev => {
      // Keep all ERP-owned events, replace/add Google-sourced ones
      const erpEvents    = prev.filter(e => e.source !== 'google')
      const googleIds    = new Set(googleEvents.map(e => e.googleEventId))
      // Also keep ERP events that have been pushed (they have googleEventId but source='erp')
      const merged = [
        ...erpEvents,
        ...googleEvents.filter(g => !erpEvents.some(e => e.googleEventId === g.googleEventId)),
      ]
      return merged
    })
  }, [])

  // Wire up Google pull → merge
  useEffect(() => {
    if (gc.connected) {
      gc.pullEvents().then(gEvents => { if (gEvents.length) mergeGoogleEvents(gEvents) })
    }
  }, [gc.connected])

  // ── Cells ─────────────────────────────────────────────────────
  const cells = getCalendarDays(viewDate.year, viewDate.month)
  const upcomingEvents = [...events]
    .filter(e => new Date(e.date) >= now && inRange(e.date))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 15)

  const eventsForDay = (d: Date) => events.filter(e => isSameDay(new Date(e.date), d))
  const prev = () => setViewDate(v => v.month === 0  ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 })
  const next = () => setViewDate(v => v.month === 11 ? { year: v.year + 1, month: 0  } : { year: v.year, month: v.month + 1 })

  // ── Add event ─────────────────────────────────────────────────
  const handleAddEvent = async (data: Omit<CalendarEvent, 'id'>) => {
    let newEv: CalendarEvent
    try {
      newEv = await createCalendarEvent(data)
    } catch {
      toast('Failed to save event', 'error')
      return
    }

    setEvents(prev => [...prev, newEv])
    scheduleReminders(newEv)

    const typeEmoji: Record<CalendarEvent['type'], string> = {
      meeting: '📅', deadline: '⏰', review: '🔍', call: '📞', holiday: '🎉',
    }
    addActivity({
      type: 'calendar_event', emoji: typeEmoji[data.type] ?? '📅',
      description: `Event added: ${data.title}`,
      detail: `${data.date}${data.time ? ' · ' + data.time : ''}${data.clientName ? ' · ' + data.clientName : ''}`,
    })

    // Push to Google Calendar
    if (gc.connected) {
      const gId = await gc.pushEvent(newEv)
      if (gId) {
        setEvents(prev => prev.map(e => e.id === newEv!.id ? { ...e, googleEventId: gId } : e))
        toast(`"${data.title}" added · synced to Google Calendar ✓`, 'success')
      } else {
        toast(`"${data.title}" added to ERP`, 'success')
      }
    } else {
      toast(`"${data.title}" added to calendar`)
    }
  }

  // ── Delete event ──────────────────────────────────────────────
  const handleDeleteEvent = async (id: string) => {
    const ev = events.find(e => e.id === id)
    try { await deleteCalendarEvent(id) } catch { toast('Delete failed', 'error'); return }
    setEvents(prev => prev.filter(e => e.id !== id))
    setDayPopover(prev => prev ? { ...prev, events: prev.events.filter(e => e.id !== id) } : null)
    cancelReminders(id)

    if (ev) {
      // Remove from Google Calendar
      if (gc.connected && ev.googleEventId) {
        await gc.removeEvent(ev.googleEventId)
        toast(`"${ev.title}" removed · deleted from Google Calendar`, 'info')
      } else {
        toast(`"${ev.title}" removed`, 'info')
      }
      addActivity({ type: 'event_deleted', emoji: '🗑️', description: `Event removed: ${ev.title}`, detail: ev.date })
    }
  }

  // ── Manual sync ───────────────────────────────────────────────
  const handleManualSync = async () => {
    const gEvents = await gc.pullEvents()
    if (gEvents.length) {
      mergeGoogleEvents(gEvents)
      toast(`Synced ${gEvents.length} events from Google Calendar`, 'success')
    } else {
      toast('No new events from Google Calendar')
    }
  }

  // ── Notification permission ───────────────────────────────────
  const handleRequestNotif = async () => {
    const granted = await requestNotificationPermission()
    setNotifGranted(granted)
    if (granted) {
      toast('Reminders enabled — you\'ll be notified before events 🔔', 'success')
      scheduleAllReminders(events)
    } else {
      toast('Notification permission denied. Enable it in browser settings.', 'error')
    }
  }

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold font-syne text-white">Calendar</h1>
        {isAdmin && (
          <button
            onClick={() => { setAddDate(undefined); setShowAdd(true) }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={15} /> Add Event
          </button>
        )}
      </div>

      {/* Google Calendar sync bar */}
      <SyncBar
        connected={gc.connected}
        connecting={gc.connecting}
        syncing={gc.syncing}
        lastSynced={gc.lastSynced}
        error={gc.error}
        configured={gc.configured}
        onConnect={gc.connect}
        onDisconnect={gc.disconnect}
        onRefresh={handleManualSync}
        notifGranted={notifGranted}
        onRequestNotif={handleRequestNotif}
      />

      <div className="flex gap-6 flex-col lg:flex-row">
        {/* Calendar grid */}
        <div className="flex-1 rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prev} className="p-2 rounded-lg transition-colors" style={{ color: '#a0a0a0' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#ffc600')}
              onMouseLeave={e => (e.currentTarget.style.color = '#a0a0a0')}>
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold font-syne text-white">
                {MONTHS[viewDate.month]} {viewDate.year}
              </h2>
              {gc.syncing && <Loader2 size={13} className="animate-spin" style={{ color: '#4285f4' }} />}
            </div>
            <button onClick={next} className="p-2 rounded-lg transition-colors" style={{ color: '#a0a0a0' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#ffc600')}
              onMouseLeave={e => (e.currentTarget.style.color = '#a0a0a0')}>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold py-2" style={{ color: '#555' }}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map(({ date, current }, i) => {
              const isToday = isSameDay(date, now)
              const dayEvs  = eventsForDay(date)
              const hasGoogle = dayEvs.some(e => e.source === 'google')
              return (
                <div key={i}
                  className="rounded-lg p-1.5 min-h-[72px] transition-colors cursor-pointer relative"
                  style={{
                    background: isToday ? 'rgba(255,198,0,0.08)' : '#0f0f0f',
                    border:     isToday ? '1px solid rgba(255,198,0,0.4)' : '1px solid transparent',
                    opacity:    current ? 1 : 0.35,
                  }}
                  onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = '#1a1a1a' }}
                  onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = '#0f0f0f' }}
                  onClick={() => current && setDayPopover({ date, events: dayEvs })}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium"
                      style={{ color: isToday ? '#ffc600' : current ? '#fff' : '#555' }}>
                      {date.getDate()}
                    </p>
                    {hasGoogle && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#4285f4' }} />
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvs.slice(0, 2).map(ev => {
                      const col = EVENT_COLORS[ev.type]
                      return (
                        <div key={ev.id} className="text-xs px-1 py-0.5 rounded truncate flex items-center gap-1"
                          style={{ background: col.bg, color: col.text, fontSize: '10px' }}>
                          {ev.source === 'google' && <span style={{ color: '#4285f4', fontWeight: 700 }}>G</span>}
                          {ev.title}
                        </div>
                      )
                    })}
                    {dayEvs.length > 2 && (
                      <div className="text-xs" style={{ color: '#555', fontSize: '10px' }}>+{dayEvs.length - 2} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,198,0,0.06)' }}>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#555' }}>
              <div className="w-2 h-2 rounded-full" style={{ background: '#ffc600' }} />
              ERP Event
            </div>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#555' }}>
              <div className="w-2 h-2 rounded-full" style={{ background: '#4285f4' }} />
              Google Calendar
            </div>
            {notifGranted && (
              <div className="flex items-center gap-1.5 text-xs" style={{ color: '#555' }}>
                <Bell size={10} style={{ color: '#ffc600' }} />
                Reminders on
              </div>
            )}
          </div>
        </div>

        {/* Upcoming sidebar */}
        <div className="lg:w-72 rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-white">Upcoming Events</h3>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600' }}>
              {upcomingEvents.length}
            </span>
          </div>

          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: '#555' }}>No upcoming events</p>
          ) : (
            <div className="space-y-2.5">
              {upcomingEvents.map(ev => {
                const col = EVENT_COLORS[ev.type]
                const d   = new Date(ev.date)
                const isGoogle = ev.source === 'google'
                return (
                  <div key={ev.id} className="flex gap-3 p-2.5 rounded-lg" style={{ background: '#1a1a1a' }}>
                    <div className="w-1 rounded-full flex-shrink-0 self-stretch" style={{ background: col.text }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-white truncate">{ev.title}</p>
                        {isGoogle && (
                          <span className="text-xs flex-shrink-0 px-1 rounded font-bold"
                            style={{ background: 'rgba(66,133,244,0.15)', color: '#4285f4', fontSize: '9px' }}>G</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="flex items-center gap-1 text-xs" style={{ color: '#a0a0a0' }}>
                          <Clock size={10} />
                          {d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          {ev.time && ` · ${ev.time}`}
                        </span>
                      </div>
                      {ev.clientName && (
                        <span className="flex items-center gap-1 text-xs mt-0.5" style={{ color: '#555' }}>
                          <User size={10} />{ev.clientName}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {dayPopover && (
        <EventPopover
          events={dayPopover.events}
          date={dayPopover.date}
          onClose={() => setDayPopover(null)}
          onDeleteEvent={handleDeleteEvent}
          isAdmin={isAdmin}
          onAddEvent={() => {
            setAddDate(dayPopover.date.toISOString().split('T')[0])
            setDayPopover(null)
            setShowAdd(true)
          }}
        />
      )}

      {showAdd && (
        <AddEventModal
          onClose={() => setShowAdd(false)}
          onAdd={handleAddEvent}
          defaultDate={addDate}
          isSyncing={gc.syncing}
        />
      )}
    </div>
  )
}
